import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ActionHelpTooltip from '../../../../shared/ui/common/ActionHelpTooltip';
import { quickFillActionTooltips } from '../../../../shared/config';
import { borderRadius, getControlBorderColor, layoutGap } from '../../../../theme/theme';
import {
  VALUE_PANEL_DESCRIPTION_ID,
  VALUE_PANEL_TITLE_ID,
  ValueSelectorPanelFooter,
  ValueSelectorPanelHeader,
} from './ValueSelectorPanelChrome';

interface ValueOption {
  value: string;
  label: string;
  description?: string;
  count?: number; // How many times this value is used in the table
}

export interface ValueSelectorPanelProps {
  columnLabel: string;
  options: ValueOption[];
  currentValue?: string;
  onSelect: (value: string) => void;
  onClearSelection: () => void;
  onClearColumn: () => void;
  onClose: () => void;
  usageStats?: Record<string, number>; // Stats on value usage in the column
  selectedCount?: number; // Number of cells selected
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

const ValueSelectorPanelSearch: React.FC<{
  columnLabel: string;
  searchText: string;
  setSearchText: (value: string) => void;
  showOnlyUsed: boolean;
  setShowOnlyUsed: (value: boolean) => void;
  usageStats: Record<string, number>;
  isShortViewport: boolean;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}> = ({
  columnLabel,
  searchText,
  setSearchText,
  showOnlyUsed,
  setShowOnlyUsed,
  usageStats,
  isShortViewport,
  searchInputRef,
}) => {
  const theme = useTheme();
  const controlBorderColor = getControlBorderColor(theme);

  return (
    <Box sx={{ px: 1, py: isShortViewport ? 0.5 : 0.75, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}` }}>
      <TextField
        inputRef={searchInputRef}
        fullWidth
        size="small"
        placeholder="Search value or description"
        inputProps={{ 'aria-label': `Search ${columnLabel} values` }}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchText && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search text"
                onClick={() => setSearchText('')}
                sx={{
                  width: 22,
                  height: 22,
                  minWidth: 22,
                  minHeight: 22,
                  p: 0,
                  m: 0,
                  border: '1px solid transparent',
                  borderRadius: borderRadius.circle,
                  color: alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.78 : 0.72),
                  backgroundColor: 'transparent',
                  '&:hover': {
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                    borderColor: 'transparent',
                  },
                  '&:focus-visible': {
                    borderColor: 'transparent',
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: 16,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: isShortViewport ? 30 : 32,
            borderRadius: `${borderRadius.none}px`,
            backgroundColor: 'transparent',
          },
        }}
      />

      {Object.keys(usageStats).length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: layoutGap.row, flexWrap: 'wrap' }}>
          <ActionHelpTooltip help={quickFillActionTooltips.showOnlyUsed}>
            <Chip
              size="small"
              icon={<FilterListIcon sx={{ fontSize: '0.82rem !important' }} />}
              clickable
              aria-label={showOnlyUsed ? 'Show all values' : 'Show only used values'}
              aria-pressed={showOnlyUsed}
              onClick={() => setShowOnlyUsed(!showOnlyUsed)}
              label={showOnlyUsed ? 'Used only' : 'All values'}
              sx={{
              height: 18,
              borderRadius: `${borderRadius.none}px`,
              fontSize: '0.66rem',
              fontWeight: 600,
              backgroundColor:
                showOnlyUsed
                  ? alpha(theme.palette.primary.main, 0.16)
                  : 'transparent',
                border: `1px solid ${controlBorderColor}`,
                color: showOnlyUsed ? theme.palette.primary.light : theme.palette.text.secondary,
                '&:hover': {
                  borderColor: controlBorderColor,
                },
              }}
            />
          </ActionHelpTooltip>
          <Typography variant="caption" color="text.secondary">
            {showOnlyUsed ? 'Only values already present in the column.' : 'Search keeps the list compact.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ValueSelectorPanelList: React.FC<{
  columnLabel: string;
  filteredOptions: ValueOption[];
  currentValue?: string;
  usageStats: Record<string, number>;
  onSelect: (value: string) => void;
}> = ({
  columnLabel,
  filteredOptions,
  currentValue,
  usageStats,
  onSelect,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        px: 0,
        py: 0,
      }}
    >
      {filteredOptions.length === 0 ? (
        <Box
          sx={{
            p: 2.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No options found
          </Typography>
        </Box>
      ) : (
        <List
          dense
          disablePadding
          aria-label={`${columnLabel} value suggestions`}
          sx={{
            display: 'block',
          }}
        >
          {filteredOptions.map((option, index) => {
            const isSelected = currentValue === option.value;
            const usageCount = usageStats[option.value] || 0;

            return (
              <ListItem key={option.value || `option-${index}`} disablePadding>
                <ListItemButton
                  onClick={() => onSelect(option.value)}
                  selected={isSelected}
                  aria-label={`Use value ${option.label} for ${columnLabel}`}
                  aria-current={isSelected ? 'true' : undefined}
                  sx={{
                    py: 0.7,
                    px: 1,
                    borderRadius: `${borderRadius.none}px`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.16)}`,
                    backgroundColor: isSelected
                      ? alpha(theme.palette.primary.main, 0.02)
                      : 'transparent',
                    boxShadow: isSelected ? `inset 2px 0 0 ${alpha(theme.palette.primary.main, 0.8)}` : 'none',
                    '&.Mui-selected': {
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      },
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: '0.73rem',
                            color: isSelected ? theme.palette.primary.light : theme.palette.text.primary,
                          }}
                        >
                          {option.label}
                        </Typography>
                        {usageCount > 0 ? (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {usageCount}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    secondary={option.description}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: {
                        mt: 0.2,
                        lineHeight: 1.4,
                        color: theme.palette.text.secondary,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

const ValueSelectorPanel: React.FC<ValueSelectorPanelProps> = ({
  columnLabel,
  options,
  currentValue,
  onSelect,
  onClearSelection,
  onClearColumn,
  onClose,
  usageStats = {},
  selectedCount = 1,
  searchInputRef,
}) => {
  const theme = useTheme();
  const isShortViewport = useMediaQuery('(max-height:480px)');
  const [searchText, setSearchText] = useState('');
  const [showOnlyUsed, setShowOnlyUsed] = useState(false);
  const [typedValue, setTypedValue] = useState(currentValue ?? '');
  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    let filtered = options;

    // Apply search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(opt =>
        opt.value.toLowerCase().includes(searchLower) ||
        opt.label.toLowerCase().includes(searchLower) ||
        opt.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply "only used" filter
    if (showOnlyUsed) {
      filtered = filtered.filter(opt => (usageStats[opt.value] || 0) > 0);
    }

    // Sort by usage count (most used first) if stats available
    if (Object.keys(usageStats).length > 0) {
      filtered = [...filtered].sort((a, b) => {
        const countA = usageStats[a.value] || 0;
        const countB = usageStats[b.value] || 0;
        if (countA === countB) {
          return a.label.localeCompare(b.label);
        }
        return countB - countA;
      });
    }

    return filtered;
  }, [options, searchText, showOnlyUsed, usageStats]);

  const handleSelect = (value: string) => {
    onSelect(value);
    setTypedValue(value);
    // Optionally clear search after selection
    setSearchText('');
  };

  const handleTypedValueApply = () => {
    if (typedValue === '') {
      return;
    }

    handleSelect(typedValue);
  };

  const totalUsed = useMemo(() => {
    return Object.values(usageStats).reduce((sum, count) => sum + count, 0);
  }, [usageStats]);

  useEffect(() => {
    setTypedValue(currentValue ?? '');
  }, [columnLabel, currentValue, selectedCount]);

  return (
    <Paper
      elevation={0}
      role="complementary"
      aria-labelledby={VALUE_PANEL_TITLE_ID}
      aria-describedby={VALUE_PANEL_DESCRIPTION_ID}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
        borderRadius: `${borderRadius.none}px`,
        overflow: 'hidden',
      }}
      data-short-viewport={isShortViewport ? 'true' : undefined}
    >
      <ValueSelectorPanelHeader
        columnLabel={columnLabel}
        selectedCount={selectedCount}
        currentValue={currentValue}
        filteredOptionsCount={filteredOptions.length}
        totalUsed={totalUsed}
        isShortViewport={isShortViewport}
        onClose={onClose}
      />
      <ValueSelectorPanelSearch
        columnLabel={columnLabel}
        searchText={searchText}
        setSearchText={setSearchText}
        showOnlyUsed={showOnlyUsed}
        setShowOnlyUsed={setShowOnlyUsed}
        usageStats={usageStats}
        isShortViewport={isShortViewport}
        searchInputRef={searchInputRef}
      />
      <ValueSelectorPanelList
        columnLabel={columnLabel}
        filteredOptions={filteredOptions}
        currentValue={currentValue}
        usageStats={usageStats}
        onSelect={handleSelect}
      />
      <ValueSelectorPanelFooter
        typedValue={typedValue}
        setTypedValue={setTypedValue}
        onApply={handleTypedValueApply}
        onClearSelection={onClearSelection}
        onClearColumn={onClearColumn}
        selectedCount={selectedCount}
        isShortViewport={isShortViewport}
      />
    </Paper>
  );
};

export default ValueSelectorPanel;
